import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Users, Heart, Sparkles, Gift, TrendingUp, Globe, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

function PlanetaryMatch() {
  const [userId] = useState(localStorage.getItem('workbook_user_id'));
  const [family, setFamily] = useState(null);
  const [matches, setMatches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  
  const [familyData, setFamilyData] = useState({
    name: '',
    location: '',
    members: [userId],
    skills_offered: [],
    needs_seeking: [],
    values: [],
    vision_statement: ''
  });
  
  const [newSkill, setNewSkill] = useState('');
  const [newNeed, setNewNeed] = useState('');
  const [newValue, setNewValue] = useState('');

  const commonSkills = [
    'Carpentry', 'Gardening', 'Teaching', 'Childcare', 'Cooking',
    'Tech Support', 'Design', 'Writing', 'Music', 'Healing Arts',
    'Building', 'Planning', 'Facilitation', 'Conflict Resolution',
    'Permaculture', 'Engineering', 'Art', 'Photography', 'Financial Planning'
  ];

  const commonValues = [
    'Partnership Culture', 'Regeneration', 'Collaboration', 'Trust',
    'Sustainability', 'Community', 'Nature Connection', 'Authenticity',
    'Service', 'Growth', 'Compassion', 'Innovation', 'Resilience'
  ];

  const createFamily = async () => {
    if (!familyData.name || !familyData.vision_statement) {
      toast.error('Please provide family name and vision');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/families`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(familyData)
      });

      if (!response.ok) throw new Error('Failed to create');

      const data = await response.json();
      setFamily(data);
      setShowCreateFamily(false);
      toast.success('Family profile created! Finding matches...');
      loadMatches(data.id);
    } catch (error) {
      toast.error('Failed to create family profile');
    }
  };

  const loadMatches = async (familyId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/families/match/${familyId}`);
      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches);
      }
    } catch (error) {
      console.error('Failed to load matches');
    }
  };

  const loadProjects = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
      }
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
      {/* Header */}
      <header className="border-b py-8 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-4">PlanetaryFam</h1>
            <p className="text-2xl text-gray-700 mb-6">Family-to-Family Matchmaking for a New Civilization</p>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              An experiment in what becomes possible when families adopt compassionate communication as foundation.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="p-8 rounded-sm bg-black text-white">
              <h3 className="text-2xl font-bold mb-4">The Foundation</h3>
              <p className="text-lg leading-relaxed mb-4">
                This platform is built on <strong>empathetic, compassionate communication</strong> — the language of NVC. 
                Before families can build together, they must first learn to <strong>repair rifts, resolve conflict, and restore trust</strong>.
              </p>
              <p className="text-lg leading-relaxed">
                When families speak the language of belonging, when they can identify needs and offer empathy, 
                everything changes. Collaboration becomes effortless. Building becomes joyful. A whole new world emerges.
              </p>
            </div>
            
            <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
              <h3 className="text-2xl font-bold mb-3">The Experiment</h3>
              <p className="text-lg leading-relaxed">
                <strong>What if every family learned NVC first?</strong> What if we could match families based on complementary gifts and needs? 
                What if trust replaced money as currency? What if giving created wealth?
              </p>
              <p className="text-lg leading-relaxed mt-4 italic">
                This is that experiment. Where compassionate communication is the prerequisite. 
                Where belonging precedes building. Where the new economy begins.
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {!family ? (
          /* Create Family Profile */
          <Card style={{ borderRadius: 0, border: '3px solid black' }}>
            <CardHeader style={{ backgroundColor: 'white' }}>
              <CardTitle className="text-4xl mb-3">Create Your Family Profile</CardTitle>
              <p className="text-lg text-gray-700">
                The foundation of the gift economy is knowing what each family offers and seeks.
              </p>
            </CardHeader>
            <CardContent className="space-y-6 mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-lg">Family/Household Name</Label>
                  <Input
                    placeholder="The Smith Family, Riverside Collective, etc."
                    value={familyData.name}
                    onChange={(e) => setFamilyData({...familyData, name: e.target.value})}
                    className="border-black text-lg py-6"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-lg">Location</Label>
                  <Input
                    placeholder="City, State or Region"
                    value={familyData.location}
                    onChange={(e) => setFamilyData({...familyData, location: e.target.value})}
                    className="border-black text-lg py-6"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-lg">Vision Statement</Label>
                <Textarea
                  placeholder="What does your family want to create? How do you want to contribute to the planetary family? What world are you building?"
                  value={familyData.vision_statement}
                  onChange={(e) => setFamilyData({...familyData, vision_statement: e.target.value})}
                  className="min-h-[120px] border-black text-lg"
                  style={{ borderRadius: 0 }}
                />
              </div>

              <div className="space-y-3">
                <Label className="text-lg">Skills & Gifts We Offer</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a skill..."
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newSkill) {
                        setFamilyData({...familyData, skills_offered: [...familyData.skills_offered, newSkill]});
                        setNewSkill('');
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      if (newSkill) {
                        setFamilyData({...familyData, skills_offered: [...familyData.skills_offered, newSkill]});
                        setNewSkill('');
                      }
                    }}
                    className="bg-black text-white"
                    style={{ borderRadius: 0 }}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {commonSkills.slice(0, 8).map(skill => (
                    <Button
                      key={skill}
                      onClick={() => setFamilyData({...familyData, skills_offered: [...familyData.skills_offered, skill]})}
                      variant="outline"
                      size="sm"
                      className="border-black"
                      style={{ borderRadius: 0 }}
                    >
                      + {skill}
                    </Button>
                  ))}
                </div>
                {familyData.skills_offered.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {familyData.skills_offered.map((skill, i) => (
                      <Badge key={i} className="bg-green-100 text-green-800 px-3 py-1">
                        {skill}
                        <button 
                          onClick={() => setFamilyData({...familyData, skills_offered: familyData.skills_offered.filter((_, idx) => idx !== i)})}
                          className="ml-2"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-lg">What We're Seeking</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a need..."
                    value={newNeed}
                    onChange={(e) => setNewNeed(e.target.value)}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newNeed) {
                        setFamilyData({...familyData, needs_seeking: [...familyData.needs_seeking, newNeed]});
                        setNewNeed('');
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      if (newNeed) {
                        setFamilyData({...familyData, needs_seeking: [...familyData.needs_seeking, newNeed]});
                        setNewNeed('');
                      }
                    }}
                    className="bg-black text-white"
                    style={{ borderRadius: 0 }}
                  >
                    Add
                  </Button>
                </div>
                {familyData.needs_seeking.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {familyData.needs_seeking.map((need, i) => (
                      <Badge key={i} className="bg-blue-100 text-blue-800 px-3 py-1">
                        {need}
                        <button 
                          onClick={() => setFamilyData({...familyData, needs_seeking: familyData.needs_seeking.filter((_, idx) => idx !== i)})}
                          className="ml-2"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-lg">Our Values</Label>
                <div className="flex flex-wrap gap-2">
                  {commonValues.map(value => (
                    <Button
                      key={value}
                      onClick={() => {
                        if (!familyData.values.includes(value)) {
                          setFamilyData({...familyData, values: [...familyData.values, value]});
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className={familyData.values.includes(value) ? 'bg-black text-white border-black' : 'border-black'}
                      style={{ borderRadius: 0 }}
                    >
                      {value}
                    </Button>
                  ))}
                </div>
              </div>

              <Button 
                onClick={createFamily}
                className="w-full bg-black text-white hover:bg-black/90 py-8 text-xl font-bold"
                style={{ borderRadius: 0 }}
              >
                <Globe className="w-6 h-6 mr-2" />
                Join the Planetary Family
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Family Profile & Matches */
          <Tabs defaultValue="matches" className="w-full">
            <TabsList className="mb-8">
              <TabsTrigger value="matches" className="text-lg px-6">Family Matches</TabsTrigger>
              <TabsTrigger value="projects" className="text-lg px-6">Collaborative Projects</TabsTrigger>
              <TabsTrigger value="gifts" className="text-lg px-6">Gift Economy</TabsTrigger>
            </TabsList>

            {/* Matches */}
            <TabsContent value="matches" className="space-y-6">
              <Card style={{ borderRadius: 0, border: '2px solid black' }}>
                <CardHeader style={{ backgroundColor: 'white' }}>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-3xl mb-2">{family.name}</CardTitle>
                      <p className="text-gray-600">{family.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Credibility Score</p>
                      <p className="text-4xl font-bold">{family.credibility_score}</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <h3 className="text-3xl font-bold">Your Family Matches</h3>
              <p className="text-lg text-gray-700">Families whose gifts complement your needs, and whose needs you can serve.</p>
              
              {matches.length === 0 ? (
                <p className="text-gray-600">Finding matches...</p>
              ) : (
                <div className="space-y-4">
                  {matches.map((match, idx) => (
                    <Card key={idx} style={{ borderRadius: 0, border: '2px solid hsl(138 30% 40%)' }}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-2xl font-bold">{match.family.name}</h4>
                            <p className="text-gray-600">{match.family.location}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Match Score</p>
                            <p className="text-3xl font-bold text-green-600">{match.match_score}</p>
                          </div>
                        </div>

                        <p className="text-base italic mb-4">"{match.family.vision_statement}"</p>

                        {match.skill_matches.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold mb-2">They offer what you need:</p>
                            <div className="flex flex-wrap gap-2">
                              {match.skill_matches.map((skill, i) => (
                                <Badge key={i} className="bg-blue-100 text-blue-800">{skill}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {match.mutual_benefits.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold mb-2">You offer what they need:</p>
                            <div className="flex flex-wrap gap-2">
                              {match.mutual_benefits.map((skill, i) => (
                                <Badge key={i} className="bg-green-100 text-green-800">{skill}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {match.shared_values.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-semibold mb-2">Shared Values:</p>
                            <div className="flex flex-wrap gap-2">
                              {match.shared_values.map((value, i) => (
                                <Badge key={i} className="bg-purple-100 text-purple-800">{value}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3 mt-4">
                          <Button className="bg-black text-white hover:bg-black/90" style={{ borderRadius: 0 }}>
                            <Heart className="w-4 h-4 mr-2" />
                            Connect
                          </Button>
                          <Button variant="outline" className="border-black" style={{ borderRadius: 0 }}>
                            View Full Profile
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Projects */}
            <TabsContent value="projects" className="space-y-6">
              <h3 className="text-3xl font-bold">Collaborative Projects</h3>
              <p className="text-lg text-gray-700">Join or propose projects where families collaborate toward shared visions.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {projects.map((project, idx) => (
                  <Card key={idx} style={{ borderRadius: 0 }}>
                    <CardHeader>
                      <CardTitle className="text-2xl">{project.title}</CardTitle>
                      <p className="text-sm text-gray-600">{project.duration} • {project.families_needed} families needed</p>
                    </CardHeader>
                    <CardContent>
                      <p className="text-base mb-4">{project.description}</p>
                      <div className="mb-4">
                        <p className="text-sm font-semibold mb-2">Skills Needed:</p>
                        <div className="flex flex-wrap gap-2">
                          {project.skills_needed.map((skill, i) => (
                            <Badge key={i}>{skill}</Badge>
                          ))}
                        </div>
                      </div>
                      <Button className="w-full bg-black text-white hover:bg-black/90" style={{ borderRadius: 0 }}>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Express Interest
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Gift Economy */}
            <TabsContent value="gifts" className="space-y-6">
              <div className="p-8 bg-white border-2 border-black">
                <h3 className="text-3xl font-bold mb-4">The Gift Economy</h3>
                <div className="space-y-4 text-lg">
                  <p>
                    <strong>Scarcity is entropy. Trust is currency. Regeneration is wealth.</strong>
                  </p>
                  <p>
                    When families give freely based on their abundance and receive based on their needs, 
                    a new economy emerges. Your credibility score grows with each contribution.
                  </p>
                  <p>
                    This isn't barter - it's <em>spiral reciprocity</em>. What you give flows outward. 
                    What you need flows inward. Trust builds. Belonging deepens.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <Card style={{ borderRadius: 0 }}>
                  <CardContent className="p-6 text-center">
                    <Gift className="w-12 h-12 mx-auto mb-3" />
                    <p className="text-4xl font-bold mb-2">{family.contributions_given || 0}</p>
                    <p className="text-sm text-gray-600">Gifts Given</p>
                  </CardContent>
                </Card>
                <Card style={{ borderRadius: 0 }}>
                  <CardContent className="p-6 text-center">
                    <Heart className="w-12 h-12 mx-auto mb-3" />
                    <p className="text-4xl font-bold mb-2">{family.contributions_received || 0}</p>
                    <p className="text-sm text-gray-600">Gifts Received</p>
                  </CardContent>
                </Card>
                <Card style={{ borderRadius: 0 }}>
                  <CardContent className="p-6 text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3" />
                    <p className="text-4xl font-bold mb-2">{family.credibility_score}</p>
                    <p className="text-sm text-gray-600">Credibility Score</p>
                  </CardContent>
                </Card>
              </div>

              <div className="p-8 rounded-sm" style={{ backgroundColor: 'hsl(138 20% 90%)', border: '2px solid hsl(138 30% 40%)' }}>
                <h4 className="text-2xl font-bold mb-4">How Credibility Grows</h4>
                <ul className="space-y-2 text-base">
                  <li>• <strong>+5 points</strong> for each gift/contribution given</li>
                  <li>• <strong>+10 points</strong> for completing collaborative projects</li>
                  <li>• <strong>+3 points</strong> for gratitude received from other families</li>
                  <li>• <strong>Trust is the foundational layer of all wealth</strong></li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

export default PlanetaryMatch;
